import { POST_TYPE_NAME } from '@frontend/app/containers/Projects/SettingsPage/Recruitment/constants';
import { GetMemberApplicantInfo_memberApplicantInfo } from '@frontend/app/queries/types/GetMemberApplicantInfo';
import { ContentType } from '@frontend/app/types/globalTypes';

export interface BriefData {
  status: string;
  statusColor: 'blue' | 'green' | 'yellow' | 'red' | 'grey';
  deliverables: string;
  compensation: {
    hasAny: boolean;
    product?: string;
    payment?: string;
    commission?: string;
  };
  briefUrl?: string;
  showViewBrief: boolean;
}

export const adaptMemberBrief = (
  memberApplicantInfo: GetMemberApplicantInfo_memberApplicantInfo | null | undefined
): BriefData => {
  if (!memberApplicantInfo?.briefTemplate?.agreementIteration) {
    return {
      status: 'Not Available',
      statusColor: 'grey',
      deliverables: '--',
      compensation: { hasAny: false },
      showViewBrief: false,
    };
  }

  const agreement = memberApplicantInfo.briefTemplate.agreementIteration;

  // Determine status and color based on available data
  const getStatusInfo = () => {
    if (agreement.briefUrl && agreement.briefUrl.trim()) {
      return { status: 'Brief Available', color: 'green' as const };
    }
    if (memberApplicantInfo.termsAccepted) {
      return { status: 'Terms Accepted', color: 'blue' as const };
    }
    return { status: 'Waiting for Agreement', color: 'blue' as const };
  };

  const statusInfo = getStatusInfo();

  // Format deliverables from content guidelines
  const formatDeliverables = () => {
    if (!agreement.contentGuidelines?.length) {
      return '--';
    }

    // Collect all accepted post types from all content guidelines
    const allAcceptedTypes = new Set<string>();

    agreement.contentGuidelines.forEach((guideline) => {
      if (guideline.acceptedPostTypes?.length) {
        guideline.acceptedPostTypes.forEach((postType) => {
          allAcceptedTypes.add(postType);
        });
      } else if (guideline.postType) {
        // Fallback to postType if acceptedPostTypes is not available
        allAcceptedTypes.add(guideline.postType);
      }
    });

    if (allAcceptedTypes.size === 0) {
      return '--';
    }

    // Convert post type codes to readable names
    const formatPostTypeName = (postType: string): string => {
      return POST_TYPE_NAME[postType as ContentType];
    };

    const formattedTypes = Array.from(allAcceptedTypes)
      .map(formatPostTypeName)
      .sort();

    return formattedTypes.join(', ');
  };

  // Format compensation by type
  const formatCompensation = () => {
    const compensation: BriefData['compensation'] = { hasAny: false };

    // 1. Free Product
    if (agreement.offersProduct) {
      if (agreement.productDescription && agreement.productDescription.trim()) {
        compensation.product = agreement.productDescription;
      } else {
        compensation.product = 'Free Product';
      }
      compensation.hasAny = true;
    }

    // 2. Payment/Amount
    if (agreement.offersPayment || (agreement.price && agreement.price > 0)) {
      compensation.payment = `$${(agreement.price || 0).toLocaleString()}`;
      compensation.hasAny = true;
    }

    // 3. Commission
    if (agreement.offersCommission) {
      if (agreement.commissionDescription && agreement.commissionDescription.trim()) {
        compensation.commission = agreement.commissionDescription;
      } else {
        compensation.commission = 'Commission';
      }
      compensation.hasAny = true;
    }

    return compensation;
  };

  // Determine if we should show the brief section
  const shouldShowBrief = () => {
    // Show if there's a valid briefUrl
    if (agreement.briefUrl && agreement.briefUrl.trim()) {
      return true;
    }
    // Show if terms are accepted or there are content guidelines
    if (memberApplicantInfo.termsAccepted || agreement.contentGuidelines?.length) {
      return true;
    }
    return false;
  };

  return {
    status: statusInfo.status,
    statusColor: statusInfo.color,
    deliverables: formatDeliverables(),
    compensation: formatCompensation(),
    briefUrl: (agreement.briefUrl && agreement.briefUrl.trim()) ? agreement.briefUrl : undefined,
    showViewBrief: shouldShowBrief(),
  };
};
